import {
  DeleteIcon,
  EditIcon,
  Group,
  InfiniteTable,
} from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  Purchase,
  PurchaseItemType,
} from '@/core/domain/purchase/Purchase.types';
import {
  useItemPurchaseRemove,
  useItemsPurchases,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import {
  PurchaseItem,
  purchaseItemTypeColor,
  purchaseItemTypeText,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getTableScrollX } from '@/utils/table';
import { Divider, Modal, Popover, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PurchaseItemSellPrice from './PurchaseItemSellPrice';
import PurchaseItemSellPriceIncrease from './PurchaseItemSellPriceIncrease';
import { Status } from '@/utils/enum/statusEnum';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';
import PurchaseItemWorkOrderLink from './PurchaseItemWorkOrderLink';
import PurchaseItemPartSwap from './PurchaseItemPartSwap';
import PurchaseItemCreatePart from './PurchaseItemCreatePart';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  purchaseId: number;
  purchaseItems?: PurchaseItem[];
  purchase?: Purchase;
  isCanceled?: boolean;
}

const PurchaseItemsPurchaseList: React.FC<Props> = ({
  purchaseId,
  isCanceled,
  purchaseItems,
  purchase,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const { data, isFetching, hasNextPage, fetchNextPage } = useItemsPurchases({
    order: 'DESC',
    purchaseId,
    enabled: !purchaseItems?.length,
  });
  const configCompany = useCompanySettings(purchase?.companyId);

  const itemsPurchase = flatInfiniteData(data);

  const removeItemPurchaseMutation = useItemPurchaseRemove();

  const handleRemoveClick = (itemPurchaseId: number) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'purchases.details.itemsPurchase.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'purchases.details.itemsPurchase.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeItemPurchaseMutation.mutateAsync({
          id: itemPurchaseId,
          purchaseId,
        });
      },
      okType: 'danger',
    });
  };

  const isPriceFieldsVisible =
    purchase?.situation === Status.open &&
    !!configCompany.data?.allowInformSellPriceInPurchaseItem;

  const lastBuyPriceColumn: ColumnsType<PurchaseItem> = isPriceFieldsVisible
    ? [
        {
          key: 'buyPriceCurrent',
          dataIndex: 'buyPriceCurrent',
          title: 'Pr. custo (R$)',
          width: 120,
          ellipsis: true,
          render: (_, item) => formatterPrice(item.buyPriceCurrent ?? 0),
        },
      ]
    : [];

  const sellPriceInformedColumn: ColumnsType<PurchaseItem> =
    isPriceFieldsVisible
      ? [
          {
            key: 'sellPriceInformed',
            dataIndex: 'sellPriceInformed',
            title: (
              <Group gap={5}>
                Pr. Venda{' '}
                <Popover
                  title="Como funciona a atualização do preço de venda"
                  content={
                    <div style={{ maxWidth: 400 }}>
                      <p>
                        Ao autorizar a compra, se a opção{' '}
                        <strong>"Alterar preço de venda"</strong> estiver
                        marcada, o sistema recalculará o{' '}
                        <strong>percentual de lucro</strong> com base no preço
                        de venda informado e no preço de custo real.
                      </p>
                      <p>
                        Essa atualização será feita apenas para os itens que
                        possuírem um preço de venda informado.
                      </p>
                    </div>
                  }
                >
                  <FaQuestionCircle size={12} />
                </Popover>
              </Group>
            ),
            width: 150,
            render: (_, item) => <PurchaseItemSellPrice item={item} />,
          },
        ]
      : [];

  const sellPriceIncreaseColumn: ColumnsType<PurchaseItem> =
    isPriceFieldsVisible
      ? [
          {
            key: 'sellPriceIncrease',
            title: 'Acrés. Pr. Venda (%)',
            width: 150,
            render: (_, item) => <PurchaseItemSellPriceIncrease item={item} />,
          },
        ]
      : [];

  const purchaseReadOnly =
    !!purchase?.boschMigration || !!purchase?.proPartsComplementId;

  const isOpened = purchase?.situation === Status.open;

  const columns: ColumnsType<PurchaseItem> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      title: intl.formatMessage({
        id: 'purchases.details.itemsPurchase.table.budget',
      }),
      ellipsis: true,
      width: 120,
      render: (value) => (value ? value : '-'),
    },
    useConfigurationColumnsPart({ isSku: true, barcode: 'barCode' }),
    {
      key: 'type',
      dataIndex: 'type',
      title: 'Tipo',
      width: 150,
      render: (type: PurchaseItemType) => (
        <StatusTag
          color={purchaseItemTypeColor[type]}
          label={purchaseItemTypeText[type]}
        />
      ),
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.description`,
      }),
      ellipsis: true,
      width: 250,
    },
    {
      key: 'manufacturingName',
      dataIndex: 'manufacturingName',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.manufacturer`,
      }),
      ellipsis: true,
      width: 200,
    },
    {
      key: 'unitMeasurement',
      dataIndex: 'unitMeasurement',
      title: 'Unidade',
      ellipsis: true,
      width: 90,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.qty`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'unitaryValue',
      dataIndex: 'unitaryValue',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.unitPrice`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'discount',
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.discount`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'totalAmount',
      dataIndex: 'totalAmount',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.amount`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    ...lastBuyPriceColumn,
    ...sellPriceInformedColumn,
    ...sellPriceIncreaseColumn,
    !purchaseItems?.length
      ? {
          key: 'actions',
          title: intl.formatMessage({ id: 'purchases.list.header.actions' }),
          render: (_, record) => (
            <Group gap={4} position="right">
              {!!purchase?.proPartsComplementId && isOpened && (
                <PurchaseItemPartSwap
                  purchaseItem={record}
                  companyId={purchase.companyId}
                />
              )}

              {!!purchase?.proPartsComplementId && !record.partId && (
                <PurchaseItemCreatePart purchaseItem={record} />
              )}

              {!!purchase?.proPartsComplementId && !!record.partId && (
                <PurchaseItemWorkOrderLink
                  complementId={purchase?.proPartsComplementId}
                  isOpened={isOpened}
                  purchaseItem={record}
                  partIdsLinked={itemsPurchase
                    .filter((item) => !!item.workOrderNumber)
                    .map((item) => item.partId)}
                />
              )}

              {record?.amountReturned > 0 && (
                <>
                  <Tooltip
                    title={`Foram devolvidas ${quantityFormatter(
                      record.amountReturned,
                    )} unidades desse item!`}
                  >
                    <FaInfoCircle size={20} color="orange" />
                  </Tooltip>

                  <Divider style={{ margin: 0 }} type="vertical" />
                </>
              )}

              <EditIcon
                iconProps={{
                  onClick: () =>
                    history.push(
                      `itemsPurchase/detailItemPurchase/item/${record.id}/detail`,
                    ),
                }}
                disabled={
                  isCanceled ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT,
                  ])
                }
              />

              <Divider style={{ margin: 0 }} type="vertical" />

              <DeleteIcon
                onClick={() => handleRemoveClick(record.id)}
                disabled={
                  isCanceled ||
                  purchase?.isGrouping ||
                  purchaseReadOnly ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_DELETE,
                  ])
                }
              />
            </Group>
          ),
          width: 135,
          fixed: 'right',
          align: 'right',
        }
      : {},
  ];

  return (
    <>
      <InfiniteTable<PurchaseItem>
        columns={columns}
        dataSource={purchaseItems?.length ? purchaseItems : itemsPurchase}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />
    </>
  );
};

export default PurchaseItemsPurchaseList;
